import type { ARScene } from "@visorxr/renderer";
import type { CaptureOptions } from "@visorxr/core";
import { composite } from "./compositor";
import { captureSize, DEFAULT_MAX_CAPTURE_EDGE, type Size } from "./layout";
import { loadOptionalWatermark } from "./load-image";

/**
 * A backend's veto on an output size, given the live drawing buffer the capture is composited from.
 * Throws to refuse. Runs inside `captureCanvasFrame`'s synchronous section, so what it inspects is
 * what the compositor actually gets.
 */
export type CaptureSizeValidator = (output: Size, live: Size) => void;

/**
 * The capture pipeline shared by every canvas-rendered backend (gyro, image tracking): render the
 * 3D/overlay content with a transparent background so the cover-fitted camera frame shows through,
 * then composite camera → scene → watermark onto one canvas. WebXR's capture is a separate path —
 * its camera source is a Raw Camera Access GL texture, not a plain `<video>`, so it doesn't share
 * this shape.
 */
export async function captureCanvasFrame(
  scene: ARScene,
  video: HTMLVideoElement,
  opts: CaptureOptions,
  validateSize?: CaptureSizeValidator,
): Promise<Blob> {
  // Every await belongs above this line. Sizing, the capture render, and the compositor's reads of
  // the drawing buffer all have to describe one instant, and only an unbroken synchronous run
  // guarantees that: the render loop keeps painting (restoring the camera background that
  // renderForCapture strips, which would then be drawn again uncropped) and an orientation change
  // can resize the buffer out from under an already-chosen output size. `composite` cooperates —
  // it draws before it yields, awaiting only the encode.
  const watermark = await loadOptionalWatermark(opts.watermarkUrl);

  const canvas = scene.renderer.domElement;
  const live = { width: canvas.width, height: canvas.height };
  const output =
    opts.width && opts.height
      ? { width: opts.width, height: opts.height }
      : captureSize(live.width, live.height, DEFAULT_MAX_CAPTURE_EDGE);
  validateSize?.(output, live);

  // preserveDrawingBuffer (set in ARScene) keeps the buffer readable for the drawImage below.
  scene.renderForCapture();
  return composite(
    { camera: video, cameraWidth: video.videoWidth, cameraHeight: video.videoHeight, scene: canvas, watermark },
    { width: output.width, height: output.height, mimeType: opts.mimeType, quality: opts.quality },
  );
}
